// Greenhold component prototype: <Certificate>. Story 3.3.
//
// Mounted at /prototype/certificate (no guard) per UX spec L444 —
// the team's MVP-Storybook substitute. Shows all 4 tier variants ×
// multiple states for visual review:
//
//   1. All 4 tiers (lg, complete, interactive, with meta strip).
//   2. Building vs complete (premium, lg) — Phase-2 vs Phase-4 staging.
//   3. Appreciation glow off vs on (founders, md) — Story 5.x preview.
//   4. Size sweep (premium, thumb / md / lg / hero).
//   5. Signed-URL vs initial-circle fallback (common, md).
//   6. Long hello-text ellipsis (preferred, md, ~300 chars).
//
// Mock fixtures use the established Caps / Faker / Sjokz / Marie naming
// from DossierShopCardPrototype.tsx (Story 2.7). picsum.photos seed
// URLs give deterministic stable images for the cards that exercise
// the vignette image path.

import { Certificate, type CertificateShare } from "@greenhold/components";

const commonShare: CertificateShare = {
  share_id: "11111111-1111-1111-1111-111111111111",
  dossier_id: "aaaaaaaa-1111-1111-1111-111111111111",
  drop_id: "dddddddd-1111-1111-1111-111111111111",
  portfolio_id: "pppppppp-1111-1111-1111-111111111111",
  portfolio_title: "Spring Showcase",
  tier: "common",
  edition_number: 87,
  share_serial: 124,
  creator_id: "cccccccc-1111-1111-1111-111111111111",
  creator_handle: "marie",
  snapshot_display_name: "Marie",
  snapshot_hello_text:
    "Glad you're here. Hope this is the first of many we share together.",
  snapshot_image_url: "creators/marie/portrait.jpg",
  snapshot_bio: "Casting analyst. Loves a good comeback story.",
  snapshot_image_signed_url: "https://picsum.photos/seed/marie/256/256",
  snapshot_image_signed_url_expires_at: "2026-05-24T18:00:00Z",
  issued_at: "2026-05-24T12:34:56Z",
  opened_at: "2026-05-24T12:35:01Z",
  is_highlighted_by_owner: false,
  is_favourited_by_owner: false,
  condition: 0,
  signed: false,
};

const preferredShare: CertificateShare = {
  share_id: "22222222-2222-2222-2222-222222222222",
  dossier_id: "aaaaaaaa-2222-2222-2222-222222222222",
  drop_id: "dddddddd-2222-2222-2222-222222222222",
  portfolio_id: "pppppppp-2222-2222-2222-222222222222",
  portfolio_title: "Worlds 26 Showdown",
  tier: "preferred",
  edition_number: 42,
  share_serial: 401,
  creator_id: "cccccccc-2222-2222-2222-222222222222",
  creator_handle: "sjokz",
  snapshot_display_name: "Sjokz",
  snapshot_hello_text:
    "I tell stories from the stage. You just collected one of them — thank you.",
  snapshot_image_url: "creators/sjokz/portrait.jpg",
  snapshot_bio: "Esports presenter, EU LCS veteran.",
  snapshot_image_signed_url: "https://picsum.photos/seed/sjokz/256/256",
  snapshot_image_signed_url_expires_at: "2026-05-24T18:00:00Z",
  issued_at: "2026-05-24T12:34:56Z",
  opened_at: "2026-05-24T12:35:01Z",
  is_highlighted_by_owner: false,
  is_favourited_by_owner: false,
  condition: 0,
  signed: false,
};

const premiumShare: CertificateShare = {
  share_id: "33333333-3333-3333-3333-333333333333",
  dossier_id: "aaaaaaaa-3333-3333-3333-333333333333",
  drop_id: "dddddddd-3333-3333-3333-333333333333",
  portfolio_id: "pppppppp-3333-3333-3333-333333333333",
  portfolio_title: "Champions Quarter",
  tier: "premium",
  edition_number: 1337,
  share_serial: 9,
  creator_id: "cccccccc-3333-3333-3333-333333333333",
  creator_handle: "caps",
  snapshot_display_name: "Caps",
  snapshot_hello_text:
    "Five-time champion. Every collector here is part of the journey.",
  snapshot_image_url: "creators/caps/portrait.jpg",
  snapshot_bio: "Mid-laner. G2 Esports.",
  snapshot_image_signed_url: "https://picsum.photos/seed/caps/256/256",
  snapshot_image_signed_url_expires_at: "2026-05-24T18:00:00Z",
  issued_at: "2026-05-24T12:34:56Z",
  opened_at: "2026-05-24T12:35:01Z",
  is_highlighted_by_owner: false,
  is_favourited_by_owner: false,
  condition: 0,
  signed: false,
};

const foundersShare: CertificateShare = {
  share_id: "44444444-4444-4444-4444-444444444444",
  dossier_id: "aaaaaaaa-4444-4444-4444-444444444444",
  drop_id: "dddddddd-4444-4444-4444-444444444444",
  portfolio_id: "pppppppp-4444-4444-4444-444444444444",
  portfolio_title: "Worlds 26 Showdown",
  tier: "founders",
  edition_number: 1,
  share_serial: 2076,
  creator_id: "cccccccc-4444-4444-4444-444444444444",
  creator_handle: "faker",
  snapshot_display_name: "Faker",
  snapshot_hello_text:
    "We won't always win, but we'll always show up. Thank you for being early.",
  snapshot_image_url: "creators/faker/portrait.jpg",
  snapshot_bio: "Mid-laner. T1. The unkillable demon king.",
  snapshot_image_signed_url: "https://picsum.photos/seed/faker/256/256",
  snapshot_image_signed_url_expires_at: "2026-05-24T18:00:00Z",
  issued_at: "2026-05-24T12:34:56Z",
  opened_at: "2026-05-24T12:35:01Z",
  is_highlighted_by_owner: false,
  is_favourited_by_owner: false,
  condition: 0,
  signed: false,
};

const longHelloShare: CertificateShare = {
  ...preferredShare,
  share_id: "55555555-5555-5555-5555-555555555555",
  snapshot_display_name: "Doinb",
  snapshot_hello_text:
    "There's something about a championship moment that lives outside time — the second you realize you're going to win, the second the crowd realizes it too, and the silence that follows that feels louder than the cheers. This share is a piece of that for me. I hope it carries the same feeling for you.",
  snapshot_image_signed_url: "https://picsum.photos/seed/doinb/256/256",
};

const noImageShare: CertificateShare = {
  ...commonShare,
  share_id: "66666666-6666-6666-6666-666666666666",
  snapshot_display_name: "Alpha",
  snapshot_image_signed_url: null,
  snapshot_image_signed_url_expires_at: null,
};

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-sm uppercase tracking-wider font-mono opacity-70 mb-3">
      {children}
    </h2>
  );
}

export function CertificatePrototype() {
  return (
    <main className="min-h-screen bg-brand-paper-100 text-brand-forest-900 p-8">
      <div className="max-w-6xl mx-auto space-y-12">
        <header>
          <h1 className="text-3xl font-heading font-bold mb-2">
            &lt;Certificate&gt; prototype
          </h1>
          <p className="opacity-80">
            Story 3.3 — the first hero artifact. Visual-review surface for
            all 4 tier variants × multiple states (UX-DR15 anatomy + epic
            AC L1342). Stories 3.6 / 4.x / 5.x compose this component as
            their hero visual.
          </p>
        </header>

        {/* Section 1 — all 4 tiers, lg, complete, interactive, showMeta. */}
        <section>
          <SectionLabel>
            Section 1 — all 4 tiers · complete state · interactive · with meta
          </SectionLabel>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 justify-items-start">
            <Certificate
              share={commonShare}
              size="lg"
              state="complete"
              interactive
              showMeta
            />
            <Certificate
              share={preferredShare}
              size="lg"
              state="complete"
              interactive
              showMeta
            />
            <Certificate
              share={premiumShare}
              size="lg"
              state="complete"
              interactive
              showMeta
            />
            <Certificate
              share={foundersShare}
              size="lg"
              state="complete"
              interactive
              showMeta
            />
          </div>
        </section>

        {/* Section 2 — building vs complete state (premium, lg). */}
        <section>
          <SectionLabel>
            Section 2 — building vs complete (Phase 2 vs Phase 4 staging
            surfaces)
          </SectionLabel>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 justify-items-start">
            <div>
              <p className="text-xs font-mono opacity-60 mb-2">state="building"</p>
              <Certificate share={premiumShare} size="lg" state="building" />
            </div>
            <div>
              <p className="text-xs font-mono opacity-60 mb-2">state="complete"</p>
              <Certificate share={premiumShare} size="lg" state="complete" />
            </div>
          </div>
        </section>

        {/* Section 3 — appreciation-glow off vs on (founders, md). */}
        <section>
          <SectionLabel>
            Section 3 — appreciation glow off / on (Story 5.x marketplace
            appreciation visual)
          </SectionLabel>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 justify-items-start">
            <div>
              <p className="text-xs font-mono opacity-60 mb-2">
                showAppreciationGlow=false
              </p>
              <Certificate share={foundersShare} size="md" />
            </div>
            <div>
              <p className="text-xs font-mono opacity-60 mb-2">
                showAppreciationGlow=true
              </p>
              <Certificate share={foundersShare} size="md" showAppreciationGlow />
            </div>
          </div>
        </section>

        {/* Section 4 — size sweep (premium, thumb / md / lg / hero). */}
        <section>
          <SectionLabel>Section 4 — size variants · thumb / md / lg / hero</SectionLabel>
          <div className="flex flex-wrap gap-8 items-start">
            <div>
              <p className="text-xs font-mono opacity-60 mb-2">thumb</p>
              <Certificate share={premiumShare} size="thumb" />
            </div>
            <div>
              <p className="text-xs font-mono opacity-60 mb-2">md</p>
              <Certificate share={premiumShare} size="md" />
            </div>
            <div>
              <p className="text-xs font-mono opacity-60 mb-2">lg</p>
              <Certificate share={premiumShare} size="lg" />
            </div>
            <div>
              <p className="text-xs font-mono opacity-60 mb-2">hero</p>
              <Certificate share={premiumShare} size="hero" />
            </div>
          </div>
        </section>

        {/* Section 5 — signed URL image vs initial-circle fallback. */}
        <section>
          <SectionLabel>
            Section 5 — signed URL vs initial fallback (vignette image
            path)
          </SectionLabel>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 justify-items-start">
            <div>
              <p className="text-xs font-mono opacity-60 mb-2">
                snapshot_image_signed_url set
              </p>
              <Certificate share={commonShare} size="md" />
            </div>
            <div>
              <p className="text-xs font-mono opacity-60 mb-2">
                snapshot_image_signed_url = null
              </p>
              <Certificate share={noImageShare} size="md" />
            </div>
          </div>
        </section>

        {/* Section 6 — long hello-text triggers line-clamp-2 + ellipsis. */}
        <section>
          <SectionLabel>
            Section 6 — long hello-text ellipsis (line-clamp-2 enforcement)
          </SectionLabel>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 justify-items-start">
            <Certificate share={longHelloShare} size="md" />
          </div>
        </section>
      </div>
    </main>
  );
}
